import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';
import WarehouseSelector from './WarehouseSelector';
import { getCurrentWarehouse } from '../services/warehouseService';
import { getCurrentUser } from '../services/employeeService';
import { SUPPORTED_LOCALES, saveLocalePreference, detectLocale } from '../i18n/i18nConfig';
import './Navigation.scss';

interface NavItem {
    title: string;
    path: string;
}

/**
 * Detect if the current session originated from AFTX Menu or FCMenu (externalized)
 */
function isAFTXRequest(): boolean {
    // Check hostname — AFTX externalized domain or FCMenu MoC domain
    const hostname = window.location.hostname;
    if (hostname.includes('aftx.amazonoperations.app') ||
        hostname.includes('aft.amazonoperations.app')) {
        return true;
    }

    // Check for aftx query parameter (e.g. linked from AFTX Menu)
    const params = new URLSearchParams(window.location.search);
    if (params.get('aftx') === 'true') {
        return true;
    }

    // Check for AFTX/FCMenu cookies (set when navigating from the menu portals)
    const cookies = document.cookie;
    if (
        cookies.includes('aftx-sensitive=') ||
        cookies.includes('aftx-nonsensitive=') ||
        cookies.includes('aftx-menu-token') ||
        cookies.includes('AFTX-Menu-Token') ||
        cookies.includes('fcmenu-sensitive=') ||
        cookies.includes('fcmenu-nonsensitive=')
    ) {
        return true;
    }

    // Check referrer from AFTX Menu portal
    const referrer = document.referrer;
    if (referrer && referrer.includes('amazonoperations.app')) {
        return true;
    }

    return false;
}

const Navigation: React.FC = () => {
    const location = useLocation();
    const [warehouseId, setWarehouseId] = useState(getCurrentWarehouse().warehouseId);
    const [isAFTX, setIsAFTX] = useState(false);
    const [username, setUsername] = useState<string>('');
    const nav = useTranslation('pages.navigation');
    const [currentLocale, setCurrentLocale] = useState(detectLocale());
    const navRef = useRef<HTMLElement>(null);

    // Keep warehouseId in sync when user changes it via the selector
    useEffect(() => {
        const onWarehouseChanged = () => setWarehouseId(getCurrentWarehouse().warehouseId);
        window.addEventListener('warehouseChanged', onWarehouseChanged);
        return () => window.removeEventListener('warehouseChanged', onWarehouseChanged);
    }, []);

    // Dynamically set body margin-top based on actual navbar height (matches legacy ng-style)
    const updateBodyPadding = useCallback(() => {
        if (navRef.current) {
            const navHeight = navRef.current.offsetHeight;
            document.body.style.marginTop = (navHeight + 10) + 'px';
        }
    }, []);

    useEffect(() => {
        setIsAFTX(isAFTXRequest());
        getCurrentUser()
            .then(userId => { if (userId) setUsername(userId); })
            .catch(() => { /* ignore — user display is non-critical */ });
    }, []);

    useEffect(() => {
        updateBodyPadding();
        window.addEventListener('resize', updateBodyPadding);
        return () => window.removeEventListener('resize', updateBodyPadding);
    }, [updateBodyPadding]);

    const handleLocaleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newLocale = e.target.value;
        saveLocalePreference(newLocale);
        setCurrentLocale(newLocale);
        // Reload to apply new locale (MbmProvider reads locale on mount)
        window.location.reload();
    };

    const navItems: NavItem[] = [
        { title: nav('nav_home'), path: `/${warehouseId}` },
        { title: nav('nav_dashboard'), path: `/${warehouseId}/dashboard` },
        { title: nav('nav_picker_eligibilities'), path: `/${warehouseId}/picker-eligibilities` },
        { title: nav('nav_cluster_eligibilities'), path: `/${warehouseId}/cluster-eligibilities` },
        { title: nav('nav_cluster_configuration'), path: `/${warehouseId}/clusters` },
        { title: nav('nav_pick_areas'), path: `/${warehouseId}/pick-areas` },
        { title: nav('nav_audit_log'), path: `/${warehouseId}/audit-log` }
    ];

    const isActive = (path: string): boolean => {
        // Match /:warehouseId exactly for home, or startsWith for sub-pages
        if (path === `/${warehouseId}`) {
            return location.pathname === `/${warehouseId}` || location.pathname === '/';
        }
        return location.pathname.startsWith(path);
    };

    return (
        <nav ref={navRef} className="navbar navbar-default navbar-fixed-top">
            <div className="container-fluid">
                <div className="navbar-header">
                    <button
                        type="button"
                        className="navbar-toggle collapsed"
                        data-toggle="collapse"
                        data-target="#navbar-collapse"
                    >
                        <span className="sr-only">{nav('toggle_navigation')}</span>
                        <span className="icon-bar"></span>
                        <span className="icon-bar"></span>
                        <span className="icon-bar"></span>
                    </button>
                    <Link className="navbar-brand" to={`/${warehouseId}`}>
                        FCEligibilityWebsite
                    </Link>
                </div>

                <div className="collapse navbar-collapse" id="navbar-collapse">
                    <ul className="nav navbar-nav navbar-left">
                        <li className="warehouse-selector-container">
                            <WarehouseSelector />
                        </li>
                    </ul>
                    <ul className="nav navbar-nav">
                        {navItems.map((item) => (
                            <li key={item.path} className={isActive(item.path) ? 'active' : ''}>
                                <Link to={item.path}>
                                    {item.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <ul className="nav navbar-nav navbar-right">
                        <li>
                            <a href="https://sim.amazon.com/issues/create?assignedFolder=b0a3edb6-7b02-4f5b-a63e-bf7d354e960d"
                               target="_blank" rel="noopener noreferrer"
                               style={{ fontSize: '12px', padding: '15px 8px' }}>
                                Feedback
                            </a>
                        </li>
                        <li>
                            <select
                                className="form-control navbar-locale-select"
                                value={currentLocale}
                                onChange={handleLocaleChange}
                                aria-label={nav('choose_language') || 'Choose Language'}
                                style={{ marginTop: '8px', width: 'auto', display: 'inline-block', fontSize: '12px', height: '34px' }}
                            >
                                {SUPPORTED_LOCALES.map(loc => (
                                    <option key={loc.code} value={loc.code}>{loc.label}</option>
                                ))}
                            </select>
                        </li>
                        {isAFTX ? (
                            /* AFTX request: show AFT-X Menu link only (matches legacy behaviour) */
                            <li>
                                <a href="https://aftx.amazonoperations.app"
                                   target="_blank" rel="noopener noreferrer">
                                    {nav('nav_aftx_menu')}
                                </a>
                            </li>
                        ) : (
                            /* Non-AFTX request: show FCConsole, FC Menu, and User Guide */
                            <>
                                <li>
                                    <a href={`https://fcdashboard.amazon.com/fc/${warehouseId}/`}
                                       target="_blank" rel="noopener noreferrer">
                                        {nav('nav_fc_console')}
                                    </a>
                                </li>
                                <li>
                                    <a href={`https://fcmenu.amazon.com/${warehouseId}`}
                                       target="_blank" rel="noopener noreferrer">
                                        {nav('nav_fc_menu')}
                                    </a>
                                </li>
                                <li>
                                    <a href="https://w.amazon.com/bin/view/FCPicking/FCEligibilityWebsiteV2/UserGuide/"
                                       target="_blank" rel="noopener noreferrer">
                                        {nav('nav_user_guide')}
                                    </a>
                                </li>
                            </>
                        )}
                        {username && (
                            <li>
                                <span className="navbar-text" style={{ marginLeft: '10px', fontSize: '12px' }}>
                                    Signed in as {username}@
                                </span>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
        </nav>
    );
};

export default Navigation;
